const ACCOUNT_PATH = /^\/app\/accounts\/([^/]+)(?=\/|$)/;
const ACCOUNT_PATTERN = '/app/accounts/:accountId';

export const isWorkspaceV2Path = path =>
  /^\/app\/accounts\/[^/]+\/v2(?:\/|$)/.test(path || '');

// Telas que pertencem ao produto novo mesmo quando abertas pelo caminho
// clássico. A casa dos relatórios é uma delas: o Hub navega por NOME de rota
// (`accountScopedRoute`), e o vue-router resolve nome para o caminho primário,
// nunca para o alias `/v2`. Resultado: saindo do Hub escuro a pessoa caía em
// `/app/accounts/:id/reports/...`, que lia a chave de tema CLÁSSICA
// (`color_scheme`) em vez da chave da V2 (`color_scheme_workspace_v2`) e
// abria claro. O tema tem que seguir a tela, não o prefixo do caminho.
const V2_THEMED_PATH = /^\/app\/accounts\/[^/]+\/reports(?:\/|$)/;

export const inheritsWorkspaceV2Theme = path =>
  isWorkspaceV2Path(path) || V2_THEMED_PATH.test(path || '');

export const workspaceV2Path = path => {
  if (isWorkspaceV2Path(path)) return path;
  return path.replace(ACCOUNT_PATH, '$&/v2');
};

export const classicWorkspacePath = path =>
  path.replace(/^(\/app\/accounts\/[^/]+)\/v2(?=\/|$)/, '$1');

// Aliases keep route names, props and permission metadata identical to V1.
export const withWorkspaceV2Aliases = routes =>
  routes.map(route => {
    const next = { ...route };
    if (
      route.path.startsWith(`${ACCOUNT_PATTERN}/`) &&
      !route.path.startsWith(`${ACCOUNT_PATTERN}/v2`)
    ) {
      const aliases = route.alias ? [route.alias].flat() : [];
      next.alias = [
        ...aliases,
        route.path.replace(ACCOUNT_PATTERN, `${ACCOUNT_PATTERN}/v2`),
      ];
    }
    if (route.children) {
      next.children = withWorkspaceV2Aliases(route.children);
    }
    return next;
  });

export const workspaceV2Redirect = (to, from, resolve) => {
  if (isWorkspaceV2Path(to.path)) return null;
  if (!ACCOUNT_PATH.test(to.path)) return null;
  const path = workspaceV2Path(to.path);
  const candidate = resolve({ path, query: to.query, hash: to.hash });
  if (!candidate.matched.length || candidate.name !== to.name) return null;
  const target = { path, query: to.query, hash: to.hash };
  // Só a primeira carga (link antigo aberto direto) troca o endereço sem deixar
  // rastro. Dentro do sistema, navegar por nome resolve para o caminho clássico
  // e passa por aqui: com replace sempre ligado, a tela anterior sumia do
  // histórico e o voltar (o da barra e o do navegador) pulava telas.
  if (!from?.matched?.length) target.replace = true;
  return target;
};

const VISUAL_KEYS =
  /^(workbench_|focus_mode_|focus_hidden_|sidebar_|conversation_sidebar_|conversation_header_|conversation_reply_|conversation_pinned_|contact_sidebar_)/;
const PANEL_KEYS = [
  'is_contact_sidebar_open',
  'is_copilot_panel_open',
  'conversation_list_font_size',
  'conversation_display_type',
];
const PREFIX = 'workspace_v2_';

export const isWorkspaceVisualKey = key =>
  VISUAL_KEYS.test(key) || /^is_.*_open$/.test(key) || PANEL_KEYS.includes(key);

export const workspaceV2Settings = settings => {
  const result = {
    ...settings,
    workbench_list_mode: 'list',
    workbench_list_width: 340,
    workbench_list_compact: false,
    conversation_list_font_size: 'md',
  };
  Object.entries(settings).forEach(([key, value]) => {
    if (key.startsWith(PREFIX)) {
      const originalKey = key.slice(PREFIX.length);
      if (isWorkspaceVisualKey(originalKey)) result[originalKey] = value;
    }
  });
  return result;
};

export const workspaceV2Delta = settings =>
  Object.fromEntries(
    Object.entries(settings).map(([key, value]) => [
      isWorkspaceVisualKey(key) ? `${PREFIX}${key}` : key,
      value,
    ])
  );

// Serialize requests per store so an older profile response cannot replace
// a newer preference in the same V2 session. Failed writes do not block the queue.
const preferenceQueues = new WeakMap();
export const enqueueWorkspacePreference = (store, persist) => {
  const previous = preferenceQueues.get(store) || Promise.resolve();
  const next = previous.catch(() => undefined).then(persist);
  preferenceQueues.set(store, next);
  return next;
};

export const fitWorkspaceListWidth = (
  desired,
  viewport,
  navigation,
  contactOpen
) => {
  const navigationWidth =
    navigation === 'sidebar' && viewport >= 1280 ? 224 : 0;
  const expandedContactWidth = viewport >= 1536 ? 360 : 320;
  const contactWidth = contactOpen ? expandedContactWidth : 0;
  const available = viewport - navigationWidth - contactWidth - 360 - 88;
  return (
    [480, 340, 256].find(width => width <= desired && width <= available) || 256
  );
};
